import React, { useMemo } from 'react';

import { FilestackImageData, FilestackImageFit } from 'api/types/shared.types';
import { Image } from 'components/Image/Image';
import { configValue } from 'utils/config';

import { getFilestackImageSrc } from './getFilestackImageSrc';

type Props = {
  data: FilestackImageData | undefined;
  alt?: string;
  height?: number;
  width?: number;
  expandSize?: boolean;
  quality?: 'high' | 'medium';
  objectFit?: 'cover' | 'contain';
  objectPosition?: 'center' | 'left';
  round?: boolean;
  fit?: FilestackImageFit;
  eagerLoad?: boolean;
  'data-qa-id'?: string;
};

export function FilestackImage({
  data,
  alt,
  width,
  height,
  expandSize,
  quality,
  objectFit,
  objectPosition,
  round,
  fit,
  eagerLoad,
  'data-qa-id': qaId,
}: Props) {
  const { src, srcSet } = getFilestackImageSrc({
    data,
    quality,
    fit,
    width: width || height ? width : data?.width,
    height: width || height ? height : data?.height,
  });

  const aspectRatio = useMemo(() => {
    // When Filestack mock is enabled we don't have the correct width and height
    if (configValue('filestack', 'mock')) return undefined;

    // When both width and height are provided there's no need to use aspect ratio.
    //
    // In the case where Filestack cuts/clips the image and changes its dimension it
    // would actually be incorrect to provide aspectRatio.
    if (width && height) return undefined;

    return data?.width && data?.height
      ? { width: data.width, height: data.height }
      : undefined;
  }, [data?.height, data?.width, height, width]);

  return (
    <Image
      src={src}
      srcSet={srcSet}
      width={width}
      height={height}
      aspectRatio={aspectRatio}
      expandSize={expandSize}
      objectFit={objectFit}
      objectPosition={objectPosition}
      round={round}
      eagerLoad={eagerLoad}
      data-qa-id={qaId}
      alt={alt || data?.altText || ''}
    />
  );
}
